import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type HotelSettingsCentralMappingMappingController from 'lumen/controllers/hotel/settings/central-mapping/mapping';
import type { Transition as TransitionWithTo } from 'lumen/services/transition';
import {
  type Breakdown,
  BREAKDOWNS,
  CHANNEL_ROOM_MAPPING_ROUTE,
  isMappingBreakdown,
  type PmsValueType,
} from 'lumen/utils/central-mapping-constants';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import type { ModelFrom } from 'lumen/utils/routes';

export type HotelSettingsCentralMappingMappingRouteModel = {
  hierarchy: Services['central-mapping']['getHierarchyTask'];
  mapping: Services['central-mapping']['getMappingEntriesTask'];
};

export default class HotelSettingsCentralMappingMappingRoute extends Route {
  queryParams = {
    categories: {
      refreshModel: true,
    },
    barBased: {
      refreshModel: true,
    },
    commissionable: {
      refreshModel: true,
    },
    breakdown: {
      refreshModel: true,
    },
    valueType: {
      refreshModel: true,
    },
    filterOption: {
      refreshModel: false,
    },
    pageSize: {
      refreshModel: true,
    },
    page: {
      refreshModel: true,
    },
    sortBy: {
      refreshModel: true,
    },
    sortDirection: {
      refreshModel: true,
    },
    search: {
      refreshModel: true,
    },
    view: {
      refreshModel: true,
    },
    includeArchived: {
      refreshModel: true,
    },
    // TODO: CEMA - To be removed after internal testing needs.
    managedBy: {
      refreshModel: true,
    },
  };

  @service declare centralMapping: Services['central-mapping'];
  @service declare featureToggle: Services['feature-toggle'];
  @service declare router: Services['router'];
  @service declare transition: Services['transition'];

  @tracked transitionHandler: ((transition: Transition) => void) | null = null;

  async beforeModel(transition: TransitionWithTo): Promise<void> {
    const { breakdown } = transition.to.queryParams as {
      breakdown: Breakdown | undefined;
    };

    if (
      breakdown === BREAKDOWNS.STAY_RESTRICTION_RATE_GROUP &&
      !this.featureToggle.hasFeatureToggle(
        FEATURE_TOGGLES.STAY_RESTRICTION_RATE_GROUP,
      )
    ) {
      this.router.transitionTo('hotel.settings.central-mapping.menu');
      return;
    }

    if (
      breakdown === BREAKDOWNS.DISTRIBUTION_CHANNEL &&
      !this.featureToggle.hasFeatureToggle(FEATURE_TOGGLES.NET_REVENUE_SETTINGS)
    ) {
      this.router.transitionTo('hotel.settings.central-mapping.menu');
      return;
    }

    const { getBreakdownsTask } = this.centralMapping;

    // Await the breakdowns task to ensure the available breakdowns are loaded before validating the query params.
    await getBreakdownsTask.last;

    this.transition.validateCentralMappingQueryParams(
      transition,
      this.paramsFor(this.routeName), // Use paramsFor to obtain the deserialised query params
    );

    if (breakdown && !isMappingBreakdown(breakdown)) {
      switch (breakdown) {
        case BREAKDOWNS.CHANNEL_ROOM_CATEGORIES:
          this.router.transitionTo(CHANNEL_ROOM_MAPPING_ROUTE);
          break;
        default:
          this.router.transitionTo('hotel.settings.central-mapping.menu');
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  async model(params: {
    barBased: string;
    breakdown: Breakdown;
    categories: string;
    commissionable: string;
    includeArchived: boolean;
    managedBy: string;
    page: number;
    pageSize: number;
    search: string;
    sortDirection: string;
    valueType: PmsValueType;
    view: string;
  }): Promise<HotelSettingsCentralMappingMappingRouteModel> {
    const {
      breakdown,
      valueType,
      pageSize,
      page,
      barBased,
      commissionable,
      categories,
      sortDirection,
      search,
      view,
      managedBy,
      includeArchived,
    } = params;

    const orderBy = `value_${sortDirection}`;

    const model: HotelSettingsCentralMappingMappingRouteModel = {
      mapping: this.centralMapping.getMappingEntriesTask,
      hierarchy: this.centralMapping.getHierarchyTask,
    };

    void model.mapping.perform({
      breakdown,
      valueType,
      pageSize,
      page,
      filterOptions: {
        orderBy,
        view,
        search,
        barBased,
        commissionable,
        categories,
      },
      managedBy,
      includeArchived,
    });

    return model;
  }

  resetController(
    controller: HotelSettingsCentralMappingMappingController,
    isExiting: boolean,
    transition: Transition,
  ): void {
    super.resetController(controller, isExiting, transition);

    if (this.transitionHandler) {
      controller.router.off('routeWillChange', this.transitionHandler);
      this.transitionHandler = null;
    }

    controller.resetMappingFilters();
  }

  setupController(
    controller: HotelSettingsCentralMappingMappingController,
    model: ModelFrom<HotelSettingsCentralMappingMappingRoute>,
    transition: Transition,
  ): void {
    super.setupController(controller, model, transition);

    if (!this.transitionHandler) {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      this.transitionHandler = (t: Transition): Promise<void> =>
        this.centralMapping.onRouteWillChange(
          this.routeName,
          controller,
          t as TransitionWithTo,
        );
      controller.router.on('routeWillChange', this.transitionHandler);
    }
  }
}
